import { t } from "@lingui/macro";
import { CheckCircleIcon, ClockIcon, StarIcon } from "@phosphor-icons/react";
import { Button } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";

export type CvOfferPlan = {
  id: string;
  name: string;
  price: number;
  credits: number;
  validity: number;
  description?: string | null;
  recommended?: boolean;
};

type CvOfferSelectorProps = {
  plans: CvOfferPlan[];
  formatPrice: (amount: number) => string;
  onContinue: (plan: CvOfferPlan) => void;
};

const splitFeatures = (description?: string | null) =>
  description
    ?.split(/[,;\n]/)
    .map((feature) => feature.trim())
    .filter(Boolean) ?? [];

const getActionLabel = (name: string) => {
  const normalized = name.trim();

  if (normalized.toLowerCase().startsWith("smart")) {
    return `Choisir ${normalized}`;
  }

  return `Choisir ${normalized}`;
};

export const CvOfferSelector = ({ plans, formatPrice, onContinue }: CvOfferSelectorProps) => {
  const defaultFeatures = [
    t`Téléchargement PDF (.pdf) haute qualité`,
    "Accès aux modèles inclus",
    "Activation immédiate après paiement",
  ];

  return (
    <section aria-labelledby="cv-offers-title" className="min-h-0">
      <h2 id="cv-offers-title" className="text-center text-sm font-bold text-[#071451]">
        Nos offres CV
      </h2>

      <p className="mx-auto mt-1 max-w-xl text-center text-xs leading-5 text-slate-500">
        Choisissez directement l’offre qui correspond à votre besoin.
      </p>

      <div className="mt-4 grid gap-3 md:grid-cols-3">
        {plans.map((plan, index) => {
          const features = splitFeatures(plan.description);

          const displayedFeatures = features.length > 0 ? features : defaultFeatures;

          return (
            <article
              key={plan.id}
              className={cn(
                "relative flex min-h-[290px] flex-col overflow-hidden rounded-2xl border bg-white p-4",
                "transition duration-200 hover:-translate-y-0.5 hover:shadow-lg",
                plan.recommended
                  ? "border-indigo-500 shadow-md ring-2 ring-indigo-100"
                  : "border-indigo-100",
              )}
            >
              {plan.recommended && (
                <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[8px] font-bold uppercase tracking-wide text-emerald-700">
                  <StarIcon aria-hidden size={9} weight="fill" />
                  Populaire
                </span>
              )}

              <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-emerald-600">
                Offre MonCVPro
              </p>

              <h3 className="mt-1.5 max-w-[70%] text-base font-bold leading-5 text-[#071451]">
                {plan.name}
              </h3>

              <p className="mt-2 text-2xl font-black tracking-tight text-indigo-700">
                {formatPrice(plan.price)}
              </p>

              <div className="mt-3 flex items-center gap-2 rounded-xl bg-indigo-50/70 px-3 py-2 text-[10px] text-indigo-900">
                <ClockIcon aria-hidden size={14} className="shrink-0 text-indigo-600" />

                <span>
                  {plan.credits.toLocaleString("fr-FR")} crédits CV · {plan.validity} jours
                </span>
              </div>

              <div className="mt-3 border-t border-slate-100 pt-3">
                <p className="text-[9px] font-bold uppercase tracking-wide text-slate-400">
                  Avantages inclus
                </p>

                <ul className="mt-2 space-y-1.5">
                  {displayedFeatures.slice(0, 6).map((feature) => (
                    <li
                      key={feature}
                      className="flex items-start gap-2 text-[10px] leading-4 text-slate-600"
                    >
                      <CheckCircleIcon
                        aria-hidden
                        size={13}
                        weight="fill"
                        className="mt-0.5 shrink-0 text-emerald-500"
                      />

                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Button
                type="button"
                onClick={() => {
                  onContinue(plan);
                }}
                className={cn(
                  "mt-auto h-10 w-full rounded-xl px-3 text-[11px] font-bold",
                  index === 1 || plan.recommended
                    ? "bg-gradient-to-r from-indigo-700 via-blue-600 to-emerald-500 text-white shadow-md"
                    : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100",
                )}
              >
                {getActionLabel(plan.name)}
              </Button>
            </article>
          );
        })}
      </div>
    </section>
  );
};
